import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { blogPosts } from '../data/content';
import { blogImageMap, images } from '../data/images';
import { ArrowLeft, Clock, Calendar, Share2 } from 'lucide-react';
import { GlowOrb } from '../components/GlowOrb';
export function BlogPost() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  // Find post or create a fallback based on slug
  const post = blogPosts.find(p => p.title.toLowerCase().replace(/\s+/g, '-') === slug) || {
    title: slug?.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') || 'Sacred Knowledge',
    date: new Date().toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    }),
    readTime: '5 min read',
    category: 'Spiritual Wellness',
    excerpt: 'Explore the depths of spiritual healing and energetic alignment in this comprehensive guide.'
  };
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  return <div className="pb-24 relative" data-id="element-360">
      <GlowOrb color="primary" size="xl" position="-top-40 -right-40" data-id="element-361" />

      {/* Hero */}
      <section className="pt-32 pb-16 relative z-10" data-id="element-362">
        <div className="container mx-auto px-6 max-w-4xl" data-id="element-363">
          <Link to="/blog" className="inline-flex items-center gap-2 text-gold hover:text-white transition-colors mb-8 text-sm font-medium uppercase tracking-wider" data-id="element-364">
            <ArrowLeft className="w-4 h-4" data-id="element-365" /> Back to Journal
          </Link>

          <div className="flex items-center gap-3 mb-6" data-id="element-366">
            <span className="px-3 py-1 rounded-full bg-white/10 border border-white/20 text-xs font-bold text-white uppercase tracking-wider" data-id="element-367">
              {post.category}
            </span>
          </div>

          <h1 className="text-4xl md:text-6xl font-serif text-white mb-8 leading-tight" data-id="element-368">
            {post.title}
          </h1>

          <div className="flex flex-wrap items-center gap-6 text-beige-muted text-sm border-b border-white/10 pb-8" data-id="element-369">
            <div className="flex items-center gap-2" data-id="element-370">
              <Calendar className="w-4 h-4 text-gold" data-id="element-371" />
              {post.date}
            </div>
            <div className="flex items-center gap-2" data-id="element-372">
              <Clock className="w-4 h-4 text-gold" data-id="element-373" />
              {post.readTime}
            </div>
            <div className="flex items-center gap-2 ml-auto" data-id="element-374">
              <button className="flex items-center gap-2 hover:text-gold transition-colors" data-id="element-375">
                <Share2 className="w-4 h-4" data-id="element-376" /> Share
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="container mx-auto px-6 max-w-4xl relative z-10" data-id="element-377">
        <div className="aspect-[21/9] rounded-3xl border border-gold/20 mb-16 overflow-hidden relative shadow-2xl shadow-primary/30" data-id="element-378">
          <div className="absolute inset-0 bg-cover bg-center" style={{
          backgroundImage: `url(${blogImageMap[post.title] || images.cosmicAbstract})`
        }} data-id="element-379"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-midnight/60 via-transparent to-transparent" data-id="element-380"></div>
          <div className="absolute top-6 left-6 w-12 h-12 border-l border-t border-gold/50" data-id="element-381"></div>
          <div className="absolute top-6 right-6 w-12 h-12 border-r border-t border-gold/50" data-id="element-382"></div>
          <div className="absolute bottom-6 left-6 w-12 h-12 border-l border-b border-gold/50" data-id="element-383"></div>
          <div className="absolute bottom-6 right-6 w-12 h-12 border-r border-b border-gold/50" data-id="element-384"></div>
        </div>

        <div className="prose prose-invert prose-lg max-w-none prose-headings:font-serif prose-headings:text-white prose-a:text-gold prose-a:no-underline hover:prose-a:text-gold-light prose-strong:text-white prose-blockquote:border-gold prose-blockquote:bg-white/5 prose-blockquote:py-2 prose-blockquote:px-6 prose-blockquote:rounded-r-xl prose-blockquote:font-serif prose-blockquote:text-xl prose-blockquote:italic" data-id="element-385">
          <p className="lead text-xl text-beige mb-8" data-id="element-386">{post.excerpt}</p>

          <p data-id="element-387">
            In our modern world, maintaining energetic hygiene is just as
            important as physical cleanliness. The subtle energies that surround
            us daily can accumulate, creating blockages that manifest as
            physical fatigue, emotional turbulence, or mental fog.
          </p>

          <h2 data-id="element-388">Understanding the Energetic Body</h2>
          <p data-id="element-389">
            Your physical body is surrounded and interpenetrated by an energy
            field, often referred to as the aura. Within this field are energy
            centers known as chakras. When these centers are open and flowing,
            we experience a state of vibrant health and deep inner peace.
          </p>

          <blockquote data-id="element-390">
            "Healing is not about fixing what is broken; it is about remembering
            what was never broken in the first place."
          </blockquote>

          <h2 data-id="element-391">Practical Steps for Daily Alignment</h2>
          <ul data-id="element-392">
            <li data-id="element-393">
              <strong data-id="element-394">Morning Grounding:</strong> Spend 5 minutes connecting
              your bare feet to the earth.
            </li>
            <li data-id="element-395">
              <strong data-id="element-396">Intentional Breathing:</strong> Practice deep belly
              breathing to reset the nervous system.
            </li>
            <li data-id="element-397">
              <strong data-id="element-398">Energetic Boundaries:</strong> Visualize a shield of
              golden light surrounding your aura before entering crowded spaces.
            </li>
          </ul>

          <p data-id="element-399">
            Remember that healing is a journey, not a destination. Be gentle
            with yourself as you navigate the layers of your own energetic
            landscape.
          </p>
        </div>

        {/* Author Bio */}
        <div className="mt-16 p-8 glass rounded-3xl flex flex-col sm:flex-row items-center gap-8" data-id="element-400">
          <div className="w-24 h-24 rounded-full bg-primary/30 border border-gold/30 flex-shrink-0 relative overflow-hidden" data-id="element-401">
            <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1544005313-94ddf0286df2?q=80&w=400&auto=format&fit=crop')] bg-cover bg-center" data-id="element-402"></div>
          </div>
          <div data-id="element-403">
            <h3 className="text-xl font-serif text-white mb-2" data-id="element-404">
              Written by Aurora Bellweather
            </h3>
            <p className="text-beige-muted text-sm leading-relaxed" data-id="element-405">
              Founder of Calmoras Healing Sanctuary. Aurora is a certified
              Master Energy Healer and Evolutionary Astrologer dedicated to
              helping souls awaken their inner light.
            </p>
          </div>
        </div>
      </section>
    </div>;
}